import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { AppNavMenu, useAppNavMenus } from '..';
import {
  ChevronDown,
  Menu01,
  ArrowRight,
  ArrowLeft,
} from '@insight-health-ai/roseate/icons';
import classNames from 'classnames';
import { MenuItem, SidenavQuickActions } from './sideNav';
import { CurrentPractitionerInfo, UserMenuContent } from './userMenu';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { Button, Drawer } from '@insight-health-ai/roseate/components';
import { useNavigate } from 'react-router-dom';
import { Global, css } from '@emotion/react';

const mobileGlobalStyles = css`
  .intercom-lightweight-app-launcher {
    bottom: 88px !important;
  }
`;

const navItemWidth = 64;
const navPaddingX = 8;
const navItemGap = 6;

const DrawerMenuItem = ({
  menu,
  navigate,
}: {
  menu: AppNavMenu;
  navigate: (path: string) => void;
}) => {
  const { title, icon, submenu } = menu;
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div key={menu.path} className="">
      <div
        onClick={() => navigate(menu.path)}
        className="flex items-center gap-2 p-2.5 cursor-pointer hover:bg-gray-100 rounded-md"
      >
        <div className="flex-shrink-0">{icon}</div>
        <span className="text-nowrap overflow-hidden text-ellipsis">
          {title}
        </span>
        {!!submenu?.length && (
          <div className="ml-auto">
            <ChevronDown
              className={classNames(
                'text-xl transform duration-200 cursor-pointer hover:bg-gray-200 rounded-md',
                isOpen ? 'rotate-180' : ''
              )}
              onClick={(e: MouseEvent<SVGSVGElement>) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
            />
          </div>
        )}
      </div>
      {isOpen && (
        <div className="flex flex-col gap-2 p-1">
          {submenu?.map((subMenu) => (
            <div
              key={subMenu.path}
              onClick={() => navigate(subMenu.path)}
              className="flex items-center gap-2 pl-8 p-2 cursor-pointer hover:bg-gray-100 rounded-md"
            >
              {subMenu.icon}
              <span className="text-nowrap overflow-hidden text-ellipsis">
                {subMenu.title}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MobileNav = () => {
  const [popupView, setPopupView] = useState<
    'navigation' | 'userOptions' | null
  >(null);

  const menus = useAppNavMenus();
  const { width, height } = useScreenSize();

  const { visibleCount, showMore } = useMemo(() => {
    // Reserve space for: padding on both sides, the "More" button
    const navWidth = width - navPaddingX * 2 - navItemWidth;

    let navItemsToRender = 0;
    let remainingWidth = navWidth;

    while (navItemsToRender < menus.length) {
      const requiredWidth = navItemWidth + navItemGap;
      if (remainingWidth < requiredWidth) break;
      remainingWidth -= requiredWidth;
      navItemsToRender++;
    }

    return { visibleCount: navItemsToRender, showMore: true };
  }, [width, menus]);

  const navigate = useNavigate();

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      setPopupView(null);
    },
    [navigate]
  );

  const renderDrawerContent = () => {
    if (popupView === 'navigation') {
      return (
        <div className="flex flex-col h-full justify-between overflow-hidden">
          <div className="p-1 h-full overflow-y-auto">
            {menus.map((menu) => {
              return (
                <DrawerMenuItem
                  key={menu.path}
                  menu={menu}
                  navigate={handleNavigate}
                />
              );
            })}
          </div>
          <div className="flex-shrink-0 mt-auto sticky bottom-0 bg-white">
            <div className="p-3 border-t border-gray-100 flex flex-col gap-2">
              <div className="text-xs font-medium uppercase text-gray-400">
                Quick Actions
              </div>
              <div className="flex items-center flex-wrap gap-2">
                <SidenavQuickActions isMobile />
              </div>
            </div>
            <div
              className="p-1 border-t border-gray-100 flex items-center gap-2 cursor-pointer"
              onClick={() => setPopupView('userOptions')}
            >
              <CurrentPractitionerInfo />
              <div className="ml-auto flex-shrink-0">
                <Button
                  variant="text"
                  appearance="secondary"
                  icon={<ArrowRight className="text-lg" />}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (popupView === 'userOptions') {
      return (
        <div className="py-2 overflow-y-auto overflow-x-hidden h-full">
          <UserMenuContent onClose={() => setPopupView(null)} isMobile />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Global styles={mobileGlobalStyles} />
      <div
        style={{ padding: `0 ${navPaddingX}px`, gap: navItemGap }}
        className="flex h-[80px] w-full overflow-hidden justify-evenly items-center flex-shrink-0 bg-white shadow-md"
      >
        {menus.slice(0, visibleCount).map((menu) => (
          <MenuItem
            key={menu.path}
            menu={menu}
            style={{ width: navItemWidth }}
            isMobile
          />
        ))}
        {showMore && (
          <MenuItem
            menu={{
              title: 'More',
              path: '',
              onClick: () => setPopupView('navigation'),
              icon: <Menu01 />,
            }}
            style={{ width: navItemWidth }}
            isMobile
          />
        )}
      </div>
      <Drawer
        title={
          popupView === 'navigation' ? (
            'Menu'
          ) : (
            <div className="flex items-center gap-4">
              <ArrowLeft
                className="text-lg cursor-pointer hover:bg-gray-100 rounded-md"
                onClick={() => setPopupView('navigation')}
              />
              Account
            </div>
          )
        }
        open={!!popupView}
        placement="bottom"
        height={
          popupView === 'navigation' ? (height > 700 ? '70%' : '85%') : 'auto'
        }
        padding={{ body: 'none', header: 'sm' }}
        onClose={() => setPopupView(null)}
      >
        {renderDrawerContent()}
      </Drawer>
    </>
  );
};
